import { useMutation as useRetailerTokenRestrictionsMutation } from "@faire/web-api/api/retailer/retailerToken/retailer-restrictions/post-hooks";
import { trackZipCodeProtectionZipcodeprotectionmodalView } from "@faire/web-api/events/zipCodeProtection/view/zipcodeprotectionmodal";
import { IGetMessengerConversationsResponse } from "@faire/web-api/indigofair/data/IGetMessengerConversationsResponse";
import { IRetailerRestrictionsRequest } from "@faire/web-api/indigofair/data/IRetailerRestrictionsRequest";
import { IRetailerRestrictionsResponse } from "@faire/web-api/indigofair/data/IRetailerRestrictionsResponse";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import debug from "debug";
import { useEffect, useState } from "react";

import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useFaireProtectionStore } from "@faire/retailer/stores/domain/FaireProtectionStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useMessengerFactory } from "@faire/retailer/stores/MessengerFactory";
import { useFetchCachedBrandView } from "@faire/retailer/views/brand/hooks/useFetchCachedBrandView";

import { IBrandPageStateContext } from "./BrandPageStateContext";

const log = debug("BRAND_PAGE");

export const useBrandPageStateData = (
  brandToken: string
): IBrandPageStateContext => {
  const { retailerToken } = useRetailerStore(["retailerToken"]);

  const { data: brandViewData, error: brandViewError } =
    useFetchCachedBrandView(brandToken ?? "");

  const [hasFaireProtection, setHasFaireProtection] = useState(false);

  const [retailerRestrictions, setRetailerRestrictions] = useState<
    IRetailerRestrictionsResponse | undefined
  >(undefined);

  const [isFaireProtectionModalClosed, setIsFaireProtectionModalClosed] =
    useState(false);
  const [isFavoriteBrand, setIsFavoriteBrand] = useState(false);

  const { mutate } = useRetailerTokenRestrictionsMutation();

  const {
    fetchData: fetchFaireProtectionData,
    isBrandTokenForbidden,
    forbiddenBrandTokens,
  } = useFaireProtectionStore([
    "fetchData",
    "isBrandTokenForbidden",
    "forbiddenBrandTokens",
  ]);

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { messengerStore } = useMessengerFactory(["messengerStore"]);

  useEffect(() => {
    if (retailerToken && isLoggedInRetailer) {
      mutate(
        [
          retailerToken,
          IRetailerRestrictionsRequest.build({
            brand_token: brandToken,
          }),
        ],
        {
          onSuccess: (response) => {
            setRetailerRestrictions(response);
          },
          onError: () => {},
        }
      );
    }
  }, [mutate, brandToken, retailerToken, isLoggedInRetailer]);

  useEffect(() => {
    fetchFaireProtectionData();
  }, [fetchFaireProtectionData]);

  useEffect(() => {
    if (
      isBrandTokenForbidden(brandToken, {
        trackReason: true,
      })
    ) {
      setHasFaireProtection(true);
      log("zip code protected is true");
      trackZipCodeProtectionZipcodeprotectionmodalView(brandToken);
    }
    // forbiddenBrandTokens is a dependency of isBrandTokenForbidden
  }, [brandToken, isBrandTokenForbidden, forbiddenBrandTokens]);

  useEffect(() => {
    if (brandViewError) {
      log("brand-view fetch failed");
      log(brandViewError);
      if (!brandViewData) {
        getLocationOrThrow().replace(faireBaseUrl());
      }
    }
  }, [brandViewData, brandViewError]);

  useEffect(() => {
    if (isLoggedInRetailer) {
      log("update messenger context");
      messengerStore.setCurrentBrandContext(
        IGetMessengerConversationsResponse.ISimpleBrand.build(
          brandViewData?.brand
        )
      );
    }
  }, [isLoggedInRetailer, messengerStore, brandViewData?.brand]);

  useEffect(() => {
    setIsFavoriteBrand(brandViewData?.brand?.is_following ?? false);
  }, [brandViewData?.brand?.is_following]);

  return {
    brandToken,
    hasFaireProtection,
    setHasFaireProtection,
    isFaireProtectionModalClosed,
    setIsFaireProtectionModalClosed,
    isFavoriteBrand,
    setIsFavoriteBrand,
    retailerRestrictions,
  };
};
